import { AppContext, SnackbarProps } from './AppContext';
import API from './api';
import { MerchantDto } from './api/generated';
import { useDidUpdate } from './hooks/useDidUpdate';
import { useEffectEvent } from './hooks/useEffectEvent';
import { useLocale } from './hooks/useLocale';
import { useStringParam } from './hooks/useSearchParam';
import { muiLocales } from './i18n';
import { muiTheme } from './muiTheme';
import { routePaths } from './router';
import { useAppSelector } from './store';
import { AuthStatuses, auth } from './utils/auth';
import { merchantHomePage } from './utils/merchant';
import { Snackbar, ThemeProvider } from '@mui/material';
import { createTheme } from '@mui/material/styles';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Outlet, useNavigate } from 'react-router-dom';

const SNACKBAR_TRANSITION_DURATION = 250;
const DEFAULT_SNACKBAR_DURATION = 3000;

const ERROR_CODE = {
  notWalletUser: 'not_wallet_user',
} as const;

export const App = () => {
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [booting, setBooting] = useState(true);
  const snackbarProps = useRef<SnackbarProps | null>();
  const { logoutReason, jwtToken } = useAppSelector((state) => state.user);
  const { t } = useTranslation();
  const locale = useLocale();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [error] = useStringParam('error');

  const themeWithLocale = useMemo(
    () => createTheme(muiTheme, muiLocales[locale]),
    [locale],
  );

  useEffect(() => {
    document.documentElement.setAttribute('lang', locale);
  }, [locale]);

  const showSnackbar = useEffectEvent(
    ({
      message,
      autoHideDuration = DEFAULT_SNACKBAR_DURATION,
    }: SnackbarProps) => {
      function showInner() {
        snackbarProps.current = {
          message,
          autoHideDuration,
        };
        setSnackbarOpen(true);
      }
      if (snackbarOpen) {
        setSnackbarOpen(false);
        setTimeout(() => {
          showInner();
        }, SNACKBAR_TRANSITION_DURATION);
      } else {
        showInner();
      }
    },
  );

  const appContextValue = useMemo(() => ({ showSnackbar }), [showSnackbar]);

  useDidUpdate(() => {
    if (logoutReason) {
      showSnackbar({ message: t(`logout_reasons.${logoutReason}`) });
      navigate(routePaths.ROOT, { replace: true });
    }
  }, [logoutReason]);

  useDidUpdate(() => {
    if (!jwtToken) queryClient.clear();
  }, [jwtToken]);

  useEffect(() => {
    auth()
      .then(async (result) => {
        if (result.status === AuthStatuses.SUCCESS) {
          queryClient.setQueryData(['merchant'], result.merchant);
        } else if (result.status === AuthStatuses.ACCESS_DENIED) {
          showSnackbar({ message: t('welcome.access_denied') });
        } else if (result.status === AuthStatuses.MERCHANT_NOT_FOUND) {
          const response = await API.Merchant.createMerchantForUser();
          queryClient.setQueryData(
            ['merchant'],
            response.data.data as MerchantDto,
          );
          navigate(merchantHomePage(response.data.data as MerchantDto), {
            replace: true,
          });
        } else if (result.status === AuthStatuses.GENERIC_ERROR) {
          showSnackbar({ message: t('common.something_went_wrong') });
        }
      })
      .finally(() => setBooting(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (error === ERROR_CODE.notWalletUser) {
      showSnackbar({ message: t('common.restricted_user') });
    }
  }, [error, showSnackbar, t]);

  return (
    <ThemeProvider theme={themeWithLocale}>
      <AppContext.Provider value={appContextValue}>
        <div className="antialiased">
          {booting ? null : <Outlet />}
          <Snackbar
            transitionDuration={SNACKBAR_TRANSITION_DURATION}
            open={snackbarOpen}
            autoHideDuration={snackbarProps.current?.autoHideDuration}
            onClose={(event, reason) => {
              if (reason === 'timeout') {
                setSnackbarOpen(false);
                snackbarProps.current = null;
              }
            }}
            message={snackbarProps.current?.message}
          />
        </div>
      </AppContext.Provider>
    </ThemeProvider>
  );
};
